import React, { useState } from 'react';
import { GroupActivity } from '@/types/groupActivity';
import GroupActivityCard from './GroupActivityCard';
import { useGroupActivityStore } from '@/store/groupActivityStore';
import { Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GroupActivityListProps {
  activities: GroupActivity[];
  isLoading?: boolean;
  onInvite?: (activity: GroupActivity) => void;
}

const GroupActivityList: React.FC<GroupActivityListProps> = ({
  activities,
  isLoading = false,
  onInvite
}) => {
  const { 
    joinGroupActivity, 
    leaveGroupActivity, 
    cancelGroupActivity,
    error
  } = useGroupActivityStore();
  
  const [loadingStates, setLoadingStates] = useState<Record<string, {
    isJoining?: boolean;
    isLeaving?: boolean;
    isCancelling?: boolean;
  }>>({});
  
  const handleJoin = async (activityId: string) => {
    setLoadingStates(prev => ({
      ...prev,
      [activityId]: { ...prev[activityId], isJoining: true }
    }));
    
    try {
      await joinGroupActivity(activityId);
    } catch (error) {
      console.error('Error joining activity:', error);
    } finally {
      setLoadingStates(prev => ({
        ...prev,
        [activityId]: { ...prev[activityId], isJoining: false }
      }));
    }
  };
  
  const handleLeave = async (activityId: string) => {
    setLoadingStates(prev => ({
      ...prev,
      [activityId]: { ...prev[activityId], isLeaving: true }
    }));
    
    try {
      await leaveGroupActivity(activityId);
    } catch (error) {
      console.error('Error leaving activity:', error);
    } finally {
      setLoadingStates(prev => ({
        ...prev,
        [activityId]: { ...prev[activityId], isLeaving: false }
      }));
    }
  };
  
  const handleCancel = async (activityId: string) => {
    setLoadingStates(prev => ({
      ...prev,
      [activityId]: { ...prev[activityId], isCancelling: true }
    }));
    
    try {
      await cancelGroupActivity(activityId);
    } catch (error) {
      console.error('Error cancelling activity:', error);
    } finally {
      setLoadingStates(prev => ({
        ...prev,
        [activityId]: { ...prev[activityId], isCancelling: false }
      }));
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 text-gold-400 animate-spin" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-500/10 text-red-500 p-4 rounded-xl border border-red-500/20 flex items-start">
        <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium">Error loading group activities</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }
  
  if (activities.length === 0) {
    return (
      <div className="text-center py-12 bg-dark-300 rounded-xl border border-dark-400">
        <p className="text-dark-800">No group activities found</p>
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {activities.map(activity => (
        <GroupActivityCard
          key={activity.id}
          activity={activity}
          onJoin={handleJoin}
          onLeave={handleLeave}
          onCancel={handleCancel}
          onInvite={onInvite}
          isJoining={loadingStates[activity.id]?.isJoining}
          isLeaving={loadingStates[activity.id]?.isLeaving}
          isCancelling={loadingStates[activity.id]?.isCancelling}
        />
      ))}
    </div>
  );
};

export default GroupActivityList;